import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Button,
  Space,
  InputNumber,
  Input,
  ColorPicker,
  Select,
  Slider,
  Upload,
  List,
  Typography,
  Empty,
  message,
} from 'antd';
import { DeleteOutlined, FontSizeOutlined, PictureOutlined } from '@ant-design/icons';
import Moveable from 'react-moveable';
import { videoAPI } from '../services/api';
import { themeColors } from '../config/theme';

const MEDIA_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4000/api').replace(/\/api\/?$/, '');

const DEFAULT_CANVAS = {
  width: 3840,
  height: 2160,
  offsetX: 0,
  offsetY: 0,
  rotation: 0,
  fitMode: 'fill',
  videoScale: 1,
};

const STAGE_MAX_WIDTH = 720;

// Common cross-platform system fonts only - no custom font files are
// bundled/embedded anywhere in this stack (web, Flutter, or screen-player),
// so a chosen family renders via whatever the OS running screen-player
// actually has installed. These are near-universal on Windows/macOS/Linux,
// which keeps that gap small in practice.
const FONT_OPTIONS = [
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Georgia',
  'Courier New',
  'Verdana',
  'Trebuchet MS',
  'Impact',
  'Comic Sans MS',
  'Tahoma',
];

const newId = () => `el_${Date.now()}_${Math.round(Math.random() * 1e6)}`;

const DEFAULT_ANIMATION = { type: 'none', duration: 2 };

const ANIMATION_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'pulse', label: 'Pulse' },
  { value: 'fade', label: 'Fade' },
  { value: 'bounce', label: 'Bounce' },
  { value: 'slideX', label: 'Slide Left/Right' },
  { value: 'slideY', label: 'Slide Up/Down' },
  { value: 'scrollLTR', label: 'Scroll Left to Right' },
  { value: 'scrollRTL', label: 'Scroll Right to Left' },
  { value: 'spin', label: 'Spin' },
];

// CSS animation names/keyframes are defined once in index.css and mirrored
// exactly in screen-player/src/App.css, so the loop previewed here matches
// what actually plays on screen. Applied to an inner wrapper (see the JSX
// below) so it never fights with the element's own static drag-set
// rotation, which stays a separate transform on the outer target div.
// Continuous, one-direction loops (a full spin, a ticker sweeping fully
// across) look correct at a constant speed; back-and-forth loops (pulse,
// fade, bounce, the small slide wobbles) look natural easing in/out at
// their turnaround points, which is what made the timing function type-
// dependent rather than a single shared "ease-in-out".
const LINEAR_ANIMATIONS = new Set(['spin', 'scrollLTR', 'scrollRTL']);

const animationStyle = (animation) => {
  if (!animation || !animation.type || animation.type === 'none') return undefined;
  const timing = LINEAR_ANIMATIONS.has(animation.type) ? 'linear' : 'ease-in-out';
  return { animation: `overlay-anim-${animation.type} ${animation.duration || 2}s ${timing} infinite` };
};

// Measures an image's real pixel dimensions so the proportional "Size"
// slider has a fixed, correct 100% reference to scale from. Falls back to
// a square guess if the browser can't load it for some reason (e.g. a
// transient network hiccup) rather than blocking the upload entirely.
const loadImageSize = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth || 200, height: img.naturalHeight || 200 });
    img.onerror = () => resolve({ width: 200, height: 200 });
    img.src = src;
  });

const defaultTextElement = () => ({
  id: newId(),
  type: 'text',
  x: 40,
  y: 40,
  width: 400,
  height: 80,
  rotation: 0,
  zIndex: 1,
  text: 'Sample text',
  fontSize: 32,
  fontFamily: 'Arial',
  color: '#ffffff',
  fontWeight: 'bold',
  textAlign: 'left',
  animation: DEFAULT_ANIMATION,
});

const defaultImageElement = (src, naturalWidth, naturalHeight) => {
  // Start the element at its natural size (capped so a huge upload doesn't
  // blow past the canvas) - naturalWidth/naturalHeight are then kept as the
  // fixed 100% reference for the proportional "Size" slider, so scaling
  // never drifts the aspect ratio no matter how many times it's adjusted.
  const scaleToFit = Math.min(1, 400 / naturalWidth, 400 / naturalHeight);
  return {
    id: newId(),
    type: 'image',
    x: 100,
    y: 100,
    width: naturalWidth * scaleToFit,
    height: naturalHeight * scaleToFit,
    naturalWidth,
    naturalHeight,
    rotation: 0,
    zIndex: 1,
    src,
    animation: DEFAULT_ANIMATION,
  };
};

// Video canvas + overlay editor: lets an admin set a per-video canvas box
// (width/height/offset/rotation, positioned within whatever region the
// screen's own video canvas already renders into) and layer free-floating
// text/image elements on top, exactly as they'll be composited live by
// screen-player. Coordinates are stored in raw canvas-pixel space so the
// editor and the player stay pixel-identical - the player just scales the
// whole frame if its actual render region differs in size.
export default function VideoOverlayEditor({ open, record, onClose, onSaved }) {
  const [canvas, setCanvas] = useState(DEFAULT_CANVAS);
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const targetRefs = useRef({});
  const stageRef = useRef(null);

  useEffect(() => {
    if (!open || !record) return;
    const existing = record.overlay_config;
    // Merge over the defaults so a layout saved before fitMode/videoScale
    // existed still gets sane values for them.
    setCanvas({ ...DEFAULT_CANVAS, ...(existing?.canvas || {}) });
    setElements(existing?.elements || []);
    setSelectedId(null);
  }, [open, record]);

  const scale = useMemo(() => Math.min(1, STAGE_MAX_WIDTH / (canvas.width || 1)), [canvas.width]);

  const selectedElement = elements.find((el) => el.id === selectedId) || null;

  const updateElement = (id, patch) => {
    setElements((prev) => prev.map((el) => (el.id === id ? { ...el, ...patch } : el)));
  };

  const addTextElement = () => {
    const el = defaultTextElement();
    setElements((prev) => [...prev, el]);
    setSelectedId(el.id);
  };

  const handleAssetUpload = async (file) => {
    setUploadingAsset(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await videoAPI.uploadOverlayAsset(formData);
      const src = `${MEDIA_BASE_URL}${response.data.data.url}`;
      const { width: naturalWidth, height: naturalHeight } = await loadImageSize(src);
      const el = defaultImageElement(src, naturalWidth, naturalHeight);
      setElements((prev) => [...prev, el]);
      setSelectedId(el.id);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploadingAsset(false);
    }
    return false;
  };

  const removeElement = (id) => {
    setElements((prev) => prev.filter((el) => el.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await videoAPI.updateOverlay(record.id, { canvas, elements });
      message.success('Overlay layout saved');
      onSaved?.();
      onClose();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save overlay layout');
    } finally {
      setSaving(false);
    }
  };

  if (!record) return null;

  return (
    <Modal
      title={`Edit Canvas & Overlays: ${record.title}`}
      open={open}
      onCancel={onClose}
      width="90vw"
      style={{ top: 20 }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          Save
        </Button>,
      ]}
      destroyOnClose
    >
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* Preview stage */}
        <div
          style={{
            flex: '1 1 auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#111',
            borderRadius: 4,
            padding: 16,
            overflow: 'auto',
          }}
        >
          {/*
            Moveable measures/positions its drag handles via
            getBoundingClientRect on the real DOM box, and its docs are
            explicit that a CSS transform (scale/rotate) sitting between its
            `container` and the dragged `target` breaks that math - corner
            handles end up clickable in the wrong place. So instead of
            shrinking the whole stage with a CSS transform, every element
            (and the video) is rendered directly at its already-scaled real
            pixel size/position - no ambient transform anywhere between
            Moveable's container and its targets. Canvas rotation is still
            fully editable and saved/applied at playback, it's just not
            simulated live here for the same reason.
          */}
          <div
            ref={stageRef}
            style={{
              position: 'relative',
              width: canvas.width * scale,
              height: canvas.height * scale,
              background: '#000',
              overflow: 'hidden',
            }}
            onMouseDown={(e) => {
              if (e.target === stageRef.current) setSelectedId(null);
            }}
          >
            {record.media_type === 'image' ? (
              <img
                key={record.id}
                alt={record.title || ''}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: canvas.fitMode === 'contain' ? 'contain' : 'cover',
                  transform: `scale(${canvas.videoScale || 1})`,
                  transformOrigin: 'center',
                }}
                src={`${MEDIA_BASE_URL}/uploads/videos/${record.filename}`}
              />
            ) : (
              <video
                key={record.id}
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: canvas.fitMode === 'contain' ? 'contain' : 'cover',
                  transform: `scale(${canvas.videoScale || 1})`,
                  transformOrigin: 'center',
                }}
                src={`${MEDIA_BASE_URL}/uploads/videos/${record.filename}`}
              />
            )}
            {elements
              .slice()
              .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
              .map((el) => (
                <div
                  key={el.id}
                  ref={(node) => {
                    targetRefs.current[el.id] = node;
                  }}
                  onMouseDown={() => setSelectedId(el.id)}
                  style={{
                    position: 'absolute',
                    left: el.x * scale,
                    top: el.y * scale,
                    width: el.width * scale,
                    height: el.height * scale,
                    transform: `rotate(${el.rotation || 0}deg)`,
                    zIndex: el.zIndex || 1,
                    outline: selectedId === el.id ? '1px dashed #1677ff' : 'none',
                    cursor: 'move',
                  }}
                >
                  {/* Animation lives on this inner wrapper, never on the
                      outer positioned div above - that div is Moveable's
                      target and its `transform: rotate()` is the static,
                      drag-set rotation; putting a looping keyframe
                      transform on the same element would fight with it. */}
                  <div style={{ width: '100%', height: '100%', ...(animationStyle(el.animation) || {}) }}>
                  {el.type === 'text' ? (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        fontSize: el.fontSize * scale,
                        fontFamily: el.fontFamily,
                        color: el.color,
                        fontWeight: el.fontWeight,
                        textAlign: el.textAlign,
                        overflow: 'hidden',
                        userSelect: 'none',
                      }}
                    >
                      {el.text}
                    </div>
                  ) : (
                    // A plain <img> here fights with Moveable's own drag
                    // handling - Chrome starts its native "drag this image
                    // out" gesture on mousedown even with draggable={false},
                    // which eats the pointer events Moveable needs to move
                    // the element. A background-image div has no such native
                    // drag behavior to fight.
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        backgroundImage: `url(${el.src})`,
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                      }}
                    />
                  )}
                  </div>
                </div>
              ))}
            {selectedElement && targetRefs.current[selectedElement.id] && (
              <Moveable
                target={targetRefs.current[selectedElement.id]}
                container={stageRef.current}
                draggable
                resizable
                rotatable
                throttleDrag={0}
                throttleResize={0}
                throttleRotate={0}
                keepRatio={false}
                origin={false}
                onDrag={({ left, top }) =>
                  updateElement(selectedElement.id, { x: left / scale, y: top / scale })
                }
                onResize={({ width, height, drag }) =>
                  updateElement(selectedElement.id, {
                    width: width / scale,
                    height: height / scale,
                    x: drag.left / scale,
                    y: drag.top / scale,
                  })
                }
                onRotate={({ rotation }) => updateElement(selectedElement.id, { rotation })}
              />
            )}
          </div>
        </div>

        {/* Side panel */}
        <div style={{ flex: '0 0 320px', maxHeight: '70vh', overflowY: 'auto' }}>
          <Typography.Title level={5}>Video Canvas</Typography.Title>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Space>
              <InputNumber
                addonBefore="W"
                value={canvas.width}
                min={1}
                onChange={(v) => setCanvas((c) => ({ ...c, width: v || 1 }))}
              />
              <InputNumber
                addonBefore="H"
                value={canvas.height}
                min={1}
                onChange={(v) => setCanvas((c) => ({ ...c, height: v || 1 }))}
              />
            </Space>
            <Space>
              <InputNumber
                addonBefore="Offset X"
                value={canvas.offsetX}
                onChange={(v) => setCanvas((c) => ({ ...c, offsetX: v || 0 }))}
              />
              <InputNumber
                addonBefore="Offset Y"
                value={canvas.offsetY}
                onChange={(v) => setCanvas((c) => ({ ...c, offsetY: v || 0 }))}
              />
            </Space>
            <InputNumber
              addonBefore="Rotation"
              addonAfter="deg"
              value={canvas.rotation}
              onChange={(v) => setCanvas((c) => ({ ...c, rotation: v || 0 }))}
              style={{ width: '100%' }}
            />
            <Select
              value={canvas.fitMode}
              style={{ width: '100%' }}
              options={[
                { value: 'fill', label: 'Fill Screen (Cover) — fills completely, may crop edges' },
                { value: 'contain', label: 'Fit to Object (Contain) — shows entire video, may add black bars' },
              ]}
              onChange={(v) => setCanvas((c) => ({ ...c, fitMode: v }))}
            />
            <div>
              <div style={{ color: themeColors.textSecondary, fontSize: 12, marginBottom: 4 }}>
                Video Scale ({(canvas.videoScale || 1).toFixed(2)}×)
              </div>
              <Slider
                min={0.1}
                max={5}
                step={0.05}
                value={canvas.videoScale}
                tooltip={{ formatter: (v) => `${v.toFixed(2)}×` }}
                onChange={(v) => setCanvas((c) => ({ ...c, videoScale: v || 1 }))}
              />
            </div>
          </Space>

          <Typography.Title level={5} style={{ marginTop: 20 }}>
            Overlay Elements
          </Typography.Title>
          <Space style={{ marginBottom: 12 }}>
            <Button icon={<FontSizeOutlined />} onClick={addTextElement}>
              Add Text
            </Button>
            <Upload accept="image/*" showUploadList={false} beforeUpload={handleAssetUpload}>
              <Button icon={<PictureOutlined />} loading={uploadingAsset}>
                Add Image
              </Button>
            </Upload>
          </Space>

          {elements.length === 0 ? (
            <Empty description="No overlay elements yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <List
              size="small"
              dataSource={elements}
              renderItem={(el) => (
                <List.Item
                  onClick={() => setSelectedId(el.id)}
                  style={{
                    cursor: 'pointer',
                    background: selectedId === el.id ? themeColors.bgElevated : 'transparent',
                    paddingLeft: 8,
                  }}
                  actions={[
                    <Button
                      key="del"
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeElement(el.id);
                      }}
                    />,
                  ]}
                >
                  {el.type === 'text' ? `Text: ${el.text?.slice(0, 20) || ''}` : 'Image'}
                </List.Item>
              )}
            />
          )}

          {selectedElement && (
            <>
              <Typography.Title level={5} style={{ marginTop: 20 }}>
                Element Properties
              </Typography.Title>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                {selectedElement.type === 'text' && (
                  <>
                    <Input.TextArea
                      rows={2}
                      value={selectedElement.text}
                      onChange={(e) => updateElement(selectedElement.id, { text: e.target.value })}
                    />
                    <Space align="center">
                      <InputNumber
                        addonBefore="Size"
                        value={selectedElement.fontSize}
                        min={4}
                        onChange={(v) => updateElement(selectedElement.id, { fontSize: v || 4 })}
                      />
                      <span style={{ color: themeColors.textSecondary }}>Text Color</span>
                      <ColorPicker
                        value={selectedElement.color}
                        onChange={(c) => updateElement(selectedElement.id, { color: c.toHexString() })}
                        showText
                      />
                    </Space>
                    <Select
                      value={selectedElement.fontFamily}
                      style={{ width: '100%', fontFamily: selectedElement.fontFamily }}
                      options={FONT_OPTIONS.map((f) => ({ value: f, label: f }))}
                      optionRender={(opt) => <span style={{ fontFamily: opt.value }}>{opt.label}</span>}
                      onChange={(v) => updateElement(selectedElement.id, { fontFamily: v })}
                    />
                    <Space>
                      <Select
                        value={selectedElement.fontWeight}
                        style={{ width: 120 }}
                        options={[
                          { value: 'normal', label: 'Normal' },
                          { value: 'bold', label: 'Bold' },
                        ]}
                        onChange={(v) => updateElement(selectedElement.id, { fontWeight: v })}
                      />
                      <Select
                        value={selectedElement.textAlign}
                        style={{ width: 120 }}
                        options={[
                          { value: 'left', label: 'Left' },
                          { value: 'center', label: 'Center' },
                          { value: 'right', label: 'Right' },
                        ]}
                        onChange={(v) => updateElement(selectedElement.id, { textAlign: v })}
                      />
                    </Space>
                  </>
                )}
                {selectedElement.type === 'image' && (() => {
                  // naturalWidth/naturalHeight are the fixed 100% reference
                  // captured at upload time; an element saved before this
                  // feature existed won't have them, so fall back to its
                  // current size as a one-off reference (the slider will
                  // read 100% for it, which is a safe, harmless default).
                  const refW = selectedElement.naturalWidth || selectedElement.width;
                  const refH = selectedElement.naturalHeight || selectedElement.height;
                  const percent = Math.round((selectedElement.width / refW) * 100);
                  return (
                    <div>
                      <div style={{ color: themeColors.textSecondary, fontSize: 12, marginBottom: 4 }}>
                        Image Size ({percent}%)
                      </div>
                      <Slider
                        min={5}
                        max={300}
                        value={percent}
                        tooltip={{ formatter: (v) => `${v}%` }}
                        onChange={(v) =>
                          updateElement(selectedElement.id, {
                            width: refW * (v / 100),
                            height: refH * (v / 100),
                            naturalWidth: refW,
                            naturalHeight: refH,
                          })
                        }
                      />
                    </div>
                  );
                })()}
                <div>
                  <div style={{ color: themeColors.textSecondary, fontSize: 12, marginBottom: 4 }}>
                    Animation (loops on screen)
                  </div>
                  <Space>
                    <Select
                      value={selectedElement.animation?.type || 'none'}
                      style={{ width: 160 }}
                      options={ANIMATION_OPTIONS}
                      onChange={(v) =>
                        updateElement(selectedElement.id, {
                          animation: { ...(selectedElement.animation || DEFAULT_ANIMATION), type: v },
                        })
                      }
                    />
                    <InputNumber
                      addonBefore="Speed"
                      addonAfter="s"
                      min={0.2}
                      max={20}
                      step={0.1}
                      value={selectedElement.animation?.duration ?? 2}
                      disabled={(selectedElement.animation?.type || 'none') === 'none'}
                      onChange={(v) =>
                        updateElement(selectedElement.id, {
                          animation: { ...(selectedElement.animation || DEFAULT_ANIMATION), duration: v || 2 },
                        })
                      }
                    />
                  </Space>
                </div>
                <Space>
                  <InputNumber
                    addonBefore="X"
                    value={Math.round(selectedElement.x)}
                    onChange={(v) => updateElement(selectedElement.id, { x: v || 0 })}
                  />
                  <InputNumber
                    addonBefore="Y"
                    value={Math.round(selectedElement.y)}
                    onChange={(v) => updateElement(selectedElement.id, { y: v || 0 })}
                  />
                </Space>
                <Space>
                  <InputNumber
                    addonBefore="W"
                    value={Math.round(selectedElement.width)}
                    min={1}
                    onChange={(v) => updateElement(selectedElement.id, { width: v || 1 })}
                  />
                  <InputNumber
                    addonBefore="H"
                    value={Math.round(selectedElement.height)}
                    min={1}
                    onChange={(v) => updateElement(selectedElement.id, { height: v || 1 })}
                  />
                </Space>
                <Space>
                  <InputNumber
                    addonBefore="Rotation"
                    value={Math.round(selectedElement.rotation || 0)}
                    onChange={(v) => updateElement(selectedElement.id, { rotation: v || 0 })}
                  />
                  <InputNumber
                    addonBefore="Layer"
                    value={selectedElement.zIndex || 1}
                    onChange={(v) => updateElement(selectedElement.id, { zIndex: v || 1 })}
                  />
                </Space>
                <Button danger icon={<DeleteOutlined />} onClick={() => removeElement(selectedElement.id)} block>
                  Remove Element
                </Button>
              </Space>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
